import {Button} from '@material-ui/core'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import {makeStyles, Theme} from '@material-ui/core/styles'
import DateRangeIcon from '@material-ui/icons/DateRange'
import {enUS, ko} from 'date-fns/locale'
import {formatDate, isValidDate} from 'helpers/dateHelper'
import React, {useEffect} from 'react'
import {DateRangePicker, Range} from 'react-date-range'
import {useTranslation} from 'react-i18next'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import {ArrowForwardRounded} from '@material-ui/icons'
import {isSameDay, subDays, subYears} from 'date-fns'

interface ParentProps {
  onChangeValue: (range: PeriodDate) => void
  dateValue: PeriodDate
  hasPlaceholder?: boolean
  minDate?: Date
  maxDate?: Date
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  parentDate: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
  },
  dateWrap: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    '& h6': {
      fontSize: 14,
      color: 'rgba(0, 0, 0, 0.54)',
    },
  },
  dateWrapActive: {
    border: '1px solid #2db1ae',
    background: '#e0e0e080',
  },
  icon: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 16,
    textAlign: 'center',
    marginRight: 10,
  },
  divider: {
    margin: 'auto',
    marginRight: 10,
    marginLeft: 10,
    height: 19,
  },
  searchButton: {
    background: 'white',
    width: 60,
    color: '#666a6c',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  dateSearchButton: {
    border: '1px solid #0cc7c7',
    borderRadius: 4,
    padding: '0 15px',
    background: 'white',
    color: '#0cc7c7',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    left: 0,
    zIndex: 500,
    width: 900,
    background: 'white',
    boxShadow: '15px 10px 30px rgba(0, 0, 0, 0.1)',
    border: '1px solid #dcdcdc',
    '& .submitButton': {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 10,
      padding: 10,
      background: '#EFF2F7',
      '& .cancelButton': {
        background: 'white',
      },
    },
  },
  dateTextWrap: {
    marginLeft: 10,
  },
  padding: {
    padding: theme.spacing(0.5),
  },
  calendarWrapper: {
    '& .rdrDayStartOfWeek .rdrInRange, .rdrDayStartOfMonth .rdrInRange': {
      left: 0,
    },
    '& .rdrStartEdge': {
      background: '#2db1ae80',
      width: 44,
      height: 44,
      top: 0,
      bottom: 0,
      borderRadius: 50,
      left: 0,
      border: '1px solid #2db1ae',
    },
    '& .rdrEndEdge': {
      background: '#2db1ae80',
      width: 44,
      height: 44,
      top: 0,
      bottom: 0,
      borderRadius: 50,
      right: 0,
      border: '1px solid #2db1ae',
    },
    '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span':
      {
        color: 'black',
      },
    '& .rdrDayNumber': {
      top: 0,
      bottom: 0,
      width: 44,
      height: 44,
    },
    '& .rdrInRange': {
      top: 0,
      bottom: 0,
      background: '#f7f7f7',
    },
    '& .rdrStartEdge.rdrEndEdge': {
      borderRadius: 50,
    },
    '& .rdrDay': {
      height: 44,
    },
    '& .rdrDayNumber span': {
      color: '#1d2429 !important',
    },
    '& .rdrDays': {
      rowGap: 5,
    },
    '& .rdrDay:has(.rdrStartEdge):not(.rdrDayPassive)': {
      borderTopLeftRadius: 50,
      borderBottomLeftRadius: 50,
      background: '#f7f7f7',
    },
    '& .rdrDay:has(.rdrEndEdge):not(.rdrDayPassive)': {
      borderTopRightRadius: 50,
      borderBottomRightRadius: 50,
      background: '#f7f7f7',
    },
    '& .rdrMonthPicker select, .rdrYearPicker select': {
      border: '1px solid gray',
    },
    '& .rdrStaticRangeSelected': {
      color: '#2db1ae !important',
    },
  },
}))

/**
 * 날짜범위선택이 가능한 캘린더
 *
 * @param onChangeValue setValue 함수
 * @param dateValue date value 값
 */

export default function RangeDatePickerEDF({
  onChangeValue,
  dateValue,
  hasPlaceholder,
  maxDate,
  minDate,
}: ParentProps) {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const [selection, setSelection] = React.useState<PeriodDate>({
    startDate: dateValue.startDate,
    endDate: dateValue.endDate,
  })
  const [open, setOpen] = React.useState(false)
  const [selectRanges, setSelectRanges] = React.useState<PeriodDate>({
    startDate: dateValue.startDate,
    endDate: dateValue.endDate,
  })
  const [placeholder, setPlaceholder] = React.useState(hasPlaceholder ?? false)
  const [isSelectStartDate, setIsSelectStartDate] = React.useState(true)
  const [inputDates, setInputDates] = React.useState({
    startDate: formatDate(dateValue.startDate),
    endDate: formatDate(dateValue.endDate),
  })

  const onRangeChange = (ranges: any) => {
    if (ranges != null) {
      if (isSelectStartDate) setIsSelectStartDate(!isSelectStartDate)
      setSelectRanges(ranges.range1)
      setInputDates({
        startDate: formatDate(ranges.range1.startDate),
        endDate: formatDate(ranges.range1.endDate),
      })
    }
  }
  const handleClose = () => {
    setOpen(false)
    setSelectRanges(selection)
  }
  const handleDataChange = () => {
    setOpen(false)
    setPlaceholder(false)
    setSelection(selectRanges)
    onChangeValue(selectRanges)
  }
  const customStaticRanges = [
    {
      label: t('IToday'),
      range: () => ({
        startDate: new Date(),
        endDate: new Date(),
      }),
      isSelected: (range: Range) =>
        isSameDay(range.startDate, new Date()) &&
        isSameDay(range.endDate, new Date()),
    },
    {
      label: t('IYesterday'),
      range: () => {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        return {
          startDate: yesterday,
          endDate: yesterday,
        }
      },
      isSelected: (range: Range) => {
        const d = subDays(new Date(), 1)
        return isSameDay(range.startDate, d) && isSameDay(range.endDate, d)
      },
    },
    {
      label: t('IPast7Days'),
      range: () => ({
        startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
        endDate: new Date(),
      }),
      isSelected: (range: Range) => {
        const start = subDays(new Date(), 6)
        const end = new Date()
        return (
          isSameDay(range.startDate, start) && isSameDay(range.endDate, end)
        )
      },
    },
    {
      label: t('IPast30Days'),
      range: () => ({
        startDate: new Date(new Date().setDate(new Date().getDate() - 29)),
        endDate: new Date(),
      }),
      isSelected: (range: Range) => {
        const start = subDays(new Date(), 29)
        const end = new Date()
        return (
          isSameDay(range.startDate, start) && isSameDay(range.endDate, end)
        )
      },
    },
    {
      label: t('IPast90Days'),
      range: () => ({
        startDate: new Date(new Date().setDate(new Date().getDate() - 89)),
        endDate: new Date(),
      }),
      isSelected: (range: Range) => {
        const start = subDays(new Date(), 89)
        const end = new Date()
        return (
          isSameDay(range.startDate, start) && isSameDay(range.endDate, end)
        )
      },
    },
    {
      label: t('IPastYear'),
      range: () => ({
        startDate: new Date(
          new Date().setFullYear(new Date().getFullYear() - 1),
        ),
        endDate: new Date(),
      }),
      isSelected: (range: Range) => {
        const start = subYears(new Date(), 1)
        const end = new Date()
        return (
          isSameDay(range.startDate, start) && isSameDay(range.endDate, end)
        )
      },
    },
  ]

  useEffect(() => {
    setSelection({
      startDate: dateValue.startDate,
      endDate: dateValue.endDate,
    })
    setSelectRanges({
      startDate: dateValue.startDate,
      endDate: dateValue.endDate,
    })
  }, [dateValue])

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={classes.parentDate}>
        <Grid
          className={`${classes.dateWrap} ${
            isSelectStartDate && open && classes.dateWrapActive
          }`}
          onClick={() => {
            setIsSelectStartDate(true)
            setOpen(true)
          }}
        >
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 120,
              height: 40,
              cursor: 'pointer',
            }}
            onClick={() => {
              setIsSelectStartDate(true)
              setOpen(true)
            }}
          >
            <div className={classes.dateTextWrap}>
              <input
                placeholder='YYYY-MM-DD'
                value={inputDates.startDate}
                style={{
                  width: 70,
                  background: isSelectStartDate && open ? '#e0e0e080' : 'white',
                }}
                onChange={(e) => {
                  setInputDates({...inputDates, startDate: e.target.value})
                  if (isValidDate(new Date(e.target.value))) {
                    setSelectRanges({
                      ...selectRanges,
                      startDate: new Date(e.target.value),
                    })
                  }
                }}
              />
            </div>
            <DateRangeIcon className={classes.icon} />
          </Grid>
        </Grid>
        <Grid
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f1f1f1',
            width: 42,
            fontSize: 24,
          }}
        >
          ~
        </Grid>
        <Grid
          className={`${classes.dateWrap} ${
            !isSelectStartDate && open && classes.dateWrapActive
          }`}
          onClick={() => {
            setIsSelectStartDate(false)
            setOpen(true)
          }}
        >
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 120,
              height: 40,
              cursor: 'pointer',
            }}
            onClick={() => {
              setIsSelectStartDate(true)
              setOpen(true)
            }}
          >
            <div className={classes.dateTextWrap}>
              {/* <Typography variant='h6'>
                {placeholder
                  ? `${t('INoticeDateRange')}`
                  : `${formatDate(selectRanges.endDate)}`}
              </Typography> */}
              <input
                placeholder='YYYY-MM-DD'
                value={inputDates.endDate}
                style={{
                  width: 70,
                  background:
                    !isSelectStartDate && open ? '#e0e0e080' : 'white',
                }}
                onChange={(e) => {
                  setInputDates({...inputDates, endDate: e.target.value})
                  if (isValidDate(new Date(e.target.value))) {
                    setSelectRanges({
                      ...selectRanges,
                      endDate: new Date(e.target.value),
                    })
                  }
                }}
              />
            </div>
            <DateRangeIcon className={classes.icon} />
          </Grid>
        </Grid>

        {open ? (
          <div className={classes.dropdown}>
            <DateRangePicker
              onChange={onRangeChange}
              retainEndDateOnFirstSelection
              focusedRange={[0, isSelectStartDate ? 0 : 1]}
              showPreview={false}
              showDateDisplay={false}
              months={2}
              minDate={minDate}
              maxDate={maxDate}
              direction='horizontal'
              scroll={{enabled: false}}
              inputRanges={[]}
              ranges={[
                {
                  startDate: selectRanges?.startDate,
                  endDate: selectRanges?.endDate,
                },
              ]}
              staticRanges={customStaticRanges}
              monthDisplayFormat={
                i18n.language === 'ko' ? 'yyyy년 M월' : 'MMM yyyy'
              }
              rangeColors={['#f7f7f7']}
              color='#2db1ae80'
              locale={i18n.language === 'ko' ? ko : enUS}
              className={classes.calendarWrapper}
            />
            <Divider />
            <div className='submitButton'>
              <Button
                variant='contained'
                color='default'
                size='small'
                disableElevation
                onClick={() => handleClose()}
                className='cancelButton'
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                disableElevation
                size='small'
                color='primary'
                onClick={() => handleDataChange()}
              >
                Apply
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </ClickAwayListener>
  )
}
